import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faCircleCheck, faCodeMerge, faPlus, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import {
  fetchTrialSets,
  fetchRuns,
  fetchComparison,
  fetchCriterionAdjudication,
  submitAdjudication,
  clearAdjudications,
  excludeCriterion,
  FACETS,
  type TrialExclusionReason,
  type TrialSetSummary,
  type RunView,
  type ComparisonOverview,
  type CriterionAdjudication,
  type ArmScore,
  type SkillFacet,
} from '../api/skillTrials';

type Toast = { kind: 'info' | 'error'; text: string };
type Draft = { id: string; surfaces: string[]; facet: SkillFacet; keep: boolean; fromA: boolean; fromB: boolean; manual?: boolean };

function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}
const pct = (x: number) => `${Math.round(x * 100)}`;

/**
 * Adjudication surface (trial-harness spec §6.1–6.6). Both arms collapse into one
 * provenance-tagged list; the curator keeps/strikes/merges/adds to build the gold
 * row; the scoreboard updates over saved criteria.
 */
export default function Adjudication() {
  const { t } = useTranslation();

  const [sets, setSets] = useState<TrialSetSummary[]>([]);
  const [setId, setSetId] = useState('');
  const [runs, setRuns] = useState<RunView[]>([]);
  const [runA, setRunA] = useState('');
  const [runB, setRunB] = useState('');

  const [overview, setOverview] = useState<ComparisonOverview | null>(null);
  const [current, setCurrent] = useState<CriterionAdjudication | null>(null);
  const [concepts, setConcepts] = useState<Draft[]>([]);
  const [mergePick, setMergePick] = useState<string | null>(null);
  const [addSurface, setAddSurface] = useState('');
  const [addFacet, setAddFacet] = useState<SkillFacet>('COMPETENCE');
  const [alt, setAlt] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const flash = useCallback((next: Toast) => setToast(next), []);
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const labelOf = useCallback((id: string) => runs.find((r) => r.id === id)?.label ?? id.slice(0, 6), [runs]);

  // Sets on mount.
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchTrialSets();
        setSets(list);
        if (list[0]) setSetId(list[0].id);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.adj.loadError')) });
      } finally {
        setLoading(false);
      }
    })();
  }, [flash, t]);

  // Runs when the set changes.
  useEffect(() => {
    if (!setId) return;
    (async () => {
      try {
        const list = await fetchRuns(setId);
        setRuns(list);
        setRunA(list[0]?.id ?? '');
        setRunB(list[1]?.id ?? '');
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.adj.loadError')) });
      }
    })();
  }, [setId, flash, t]);

  const loadComparison = useCallback(
    async (a: string, b: string, startAt = 0) => {
      if (!a || !b || a === b) {
        setOverview(null);
        setCurrent(null);
        return;
      }
      setLoading(true);
      try {
        const ov = await fetchComparison(a, b);
        setOverview(ov);
        const pending = ov.pending.pending;
        if (pending[startAt]) {
          const view = await fetchCriterionAdjudication(a, b, pending[startAt]);
          setCurrent(view);
          setConcepts(seedConcepts(view));
        } else {
          setCurrent(null);
        }
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.adj.loadError')) });
      } finally {
        setLoading(false);
      }
    },
    [flash, t]
  );

  useEffect(() => {
    void loadComparison(runA, runB, 0);
  }, [runA, runB, loadComparison]);

  const done = !!overview && overview.pending.pending.length === 0;
  const kept = useMemo(() => concepts.filter((c) => c.keep), [concepts]);

  // --- concept editing ------------------------------------------------------

  const toggleKeep = (id: string) =>
    setConcepts((prev) => prev.map((c) => (c.id === id ? { ...c, keep: !c.keep } : c)));

  const setFacet = (id: string, facet: SkillFacet) =>
    setConcepts((prev) => prev.map((c) => (c.id === id ? { ...c, facet } : c)));

  const clickMerge = (id: string) => {
    if (mergePick === null) return setMergePick(id);
    if (mergePick === id) return setMergePick(null);
    setConcepts((prev) => {
      const a = prev.find((c) => c.id === mergePick);
      const b = prev.find((c) => c.id === id);
      if (!a || !b) return prev;
      const merged: Draft = {
        ...a,
        surfaces: [...a.surfaces, ...b.surfaces.filter((s) => !a.surfaces.some((x) => x.toLowerCase() === s.toLowerCase()))],
        fromA: a.fromA || b.fromA,
        fromB: a.fromB || b.fromB,
        keep: true,
      };
      return prev.map((c) => (c.id === mergePick ? merged : c)).filter((c) => c.id !== id);
    });
    setMergePick(null);
  };

  const addAlt = (id: string) => {
    const s = (alt[id] ?? '').trim();
    if (!s) return;
    setConcepts((prev) =>
      prev.map((c) =>
        c.id === id && !c.surfaces.some((x) => x.toLowerCase() === s.toLowerCase())
          ? { ...c, surfaces: [...c.surfaces, s] }
          : c
      )
    );
    setAlt((prev) => ({ ...prev, [id]: '' }));
  };

  const removeSurface = (id: string, i: number) =>
    setConcepts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, surfaces: c.surfaces.filter((_, idx) => idx !== i) } : c)).filter((c) => c.surfaces.length > 0)
    );

  const addConcept = () => {
    const s = addSurface.trim();
    if (!s) return;
    setConcepts((prev) => [...prev, { id: crypto.randomUUID(), surfaces: [s], facet: addFacet, keep: true, fromA: false, fromB: false, manual: true }]);
    setAddSurface('');
  };

  // --- submit ---------------------------------------------------------------

  const submit = useCallback(async () => {
    if (!current || kept.length === 0) return;
    setSaving(true);
    try {
      await submitAdjudication(runA, runB, {
        criterion_id: current.criterion_id,
        criterion_version: current.criterion_version,
        concepts: kept.map((c) => ({ expected_facet: c.facet, surfaces: c.surfaces })),
      });
      flash({ kind: 'info', text: t('skillTrials.adj.saved') });
      await loadComparison(runA, runB, 0); // refetch: scoreboard + pending both move
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('skillTrials.adj.saveError')) });
    } finally {
      setSaving(false);
    }
  }, [current, kept, runA, runB, flash, t, loadComparison]);

  const exclude = useCallback(
    async (reason: TrialExclusionReason) => {
      if (!current) return;
      try {
        await excludeCriterion(runA, runB, { criterion_id: current.criterion_id, reason });
        flash({ kind: 'info', text: t('skillTrials.adj.excluded') });
        await loadComparison(runA, runB, 0);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.adj.excludeError')) });
      }
    },
    [current, runA, runB, t, flash, loadComparison]
  );

  const clearAdj = useCallback(async () => {
    if (!setId) return;
    if (!window.confirm(t('skillTrials.adj.clearConfirm'))) return;
    try {
      const { deleted } = await clearAdjudications(setId);
      flash({ kind: 'info', text: t('skillTrials.adj.cleared', { n: deleted }) });
      await loadComparison(runA, runB, 0);
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('skillTrials.adj.clearError')) });
    }
  }, [setId, runA, runB, t, flash, loadComparison]);

  const provenance = (c: Draft): string => {
    if (c.manual) return t('skillTrials.adj.added');
    if (c.fromA && c.fromB) return `${labelOf(runA)} + ${labelOf(runB)}`;
    if (c.fromA) return labelOf(runA);
    if (c.fromB) return labelOf(runB);
    return '—';
  };

  return (
    <div className="mx-auto max-w-[900px] px-4 py-6" style={{ color: '#0f172a' }}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('skillTrials.adj.title')}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{t('skillTrials.adj.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Select value={setId} onChange={setSetId} options={sets.map((s) => ({ v: s.id, l: s.label }))} label={t('skillTrials.adj.set')} />
          <Select value={runA} onChange={setRunA} options={runs.map((r) => ({ v: r.id, l: r.label }))} label="A" />
          <Select value={runB} onChange={setRunB} options={runs.map((r) => ({ v: r.id, l: r.label }))} label="B" />
        </div>
      </div>

      {setId && (
        <div className="mb-3 flex justify-end">
          <button onClick={clearAdj} className="text-[12px] text-red-600 hover:underline">
            {t('skillTrials.adj.clearAdjudications')}
          </button>
        </div>
      )}

      {/* Scoreboard */}
      {overview && (
        <div className="mb-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ArmCard score={overview.scoreboard.arm_a} />
          <ArmCard score={overview.scoreboard.arm_b} />
        </div>
      )}
      {overview && (
        <p className="mb-4 text-[11.5px] text-slate-400">
          {t('skillTrials.adj.liveNote', { n: overview.scoreboard.adjudicated_criteria })}
        </p>
      )}

      {loading ? (
        <Card>{t('common.loading')}</Card>
      ) : !runA || !runB || runA === runB ? (
        <Card>{t('skillTrials.adj.pickTwo')}</Card>
      ) : done ? (
        <div className="flex items-center justify-center gap-3 rounded-[14px] border bg-white py-12 text-slate-600 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-600" />
          {t('skillTrials.adj.allDone', {
            adjudicated: overview?.pending.adjudicated ?? 0,
            skipped: overview?.pending.agreement_skipped ?? 0,
          })}
        </div>
      ) : current ? (
        <>
          {/* Criterion */}
          <div className="mb-4 rounded-[14px] border bg-white p-5 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Chip>{current.language}</Chip>
              <Chip>{current.kind}</Chip>
              <span className="ml-auto text-[11px] text-slate-400">
                {(overview?.pending.adjudicated ?? 0) + 1} / {overview?.pending.total ?? 0}
              </span>
            </div>
            <p className="font-serif text-[16px] leading-relaxed">{current.text}</p>
          </div>

          {/* Merge hint */}
          {mergePick && (
            <div className="mb-2 flex items-center gap-2 px-1 text-[12px] text-teal-700">
              <FontAwesomeIcon icon={faCodeMerge} />
              {t('skillTrials.adj.mergeHint')}
              <button className="underline" onClick={() => setMergePick(null)}>{t('common.cancel')}</button>
            </div>
          )}

          {/* Concept list */}
          <ul className="mb-3 flex flex-col gap-2">
            {concepts.map((c) => (
              <li
                key={c.id}
                className={`flex flex-wrap items-center gap-2.5 rounded-xl border px-3 py-2.5 shadow-sm ${
                  mergePick === c.id ? 'bg-teal-50' : 'bg-white'
                } ${c.keep ? '' : 'opacity-55'}`}
                style={{ borderColor: mergePick === c.id ? '#14b8a6' : '#e2e8f0' }}
              >
                <span className="min-w-[92px] rounded-md px-2 py-0.5 text-center font-mono text-[10px] font-semibold"
                  style={provStyle(c)}>
                  {provenance(c)}
                </span>
                <select
                  className="rounded-md border px-1.5 py-1 font-mono text-[10px] text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  style={{ borderColor: '#e2e8f0' }}
                  value={c.facet}
                  onChange={(e) => setFacet(c.id, e.target.value as SkillFacet)}
                >
                  {FACETS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <div className="flex flex-1 flex-wrap items-center gap-1.5">
                  {c.surfaces.map((s, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 text-sm ${c.keep ? '' : 'line-through'}`}>
                      {i > 0 && <span className="text-slate-400">·</span>}
                      <span className={i === 0 ? 'font-medium' : 'text-slate-500'}>{s}</span>
                      <button className="text-slate-300 hover:text-red-500" onClick={() => removeSurface(c.id, i)} aria-label={t('skillTrials.adj.remove')}>
                        <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                      </button>
                    </span>
                  ))}
                  <input
                    className="w-32 rounded-md border px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-400"
                    style={{ borderColor: '#e2e8f0' }}
                    placeholder={t('skillTrials.adj.altSurface')}
                    value={alt[c.id] ?? ''}
                    onChange={(e) => setAlt((p) => ({ ...p, [c.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && addAlt(c.id)}
                  />
                </div>
                <button
                  className={`mini rounded-md border px-2 py-1 text-[11px] ${mergePick === c.id ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  style={{ borderColor: '#cbd5e1' }}
                  onClick={() => clickMerge(c.id)}
                >
                  <FontAwesomeIcon icon={faCodeMerge} className="mr-1 text-[10px]" />
                  {t('skillTrials.adj.merge')}
                </button>
                <button
                  className="rounded-md border px-2 py-1 text-[11px]"
                  style={{ borderColor: '#cbd5e1', color: c.keep ? '#bd4a4a' : '#2c9068' }}
                  onClick={() => toggleKeep(c.id)}
                >
                  <FontAwesomeIcon icon={c.keep ? faXmark : faRotateLeft} className="mr-1 text-[10px]" />
                  {c.keep ? t('skillTrials.adj.strike') : t('skillTrials.adj.restore')}
                </button>
              </li>
            ))}
          </ul>

          {/* Empty passes (§6.5) */}
          {current.empty_passes.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2 px-1">
              {current.empty_passes.map((e, i) => (
                <span key={i} className="rounded-md bg-slate-50 px-2 py-0.5 font-mono text-[10px] text-slate-400" style={{ border: '1px dashed #cbd5e1' }}>
                  {labelOf(e.arm === 'A' ? runA : runB)} · {e.facet.toLowerCase()} — —
                </span>
              ))}
            </div>
          )}

          {/* Add missing + save */}
          <div className="mb-4 rounded-[14px] border bg-slate-50 p-3 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold text-slate-500">{t('skillTrials.adj.addMissing')}</span>
              <input
                className="min-w-[180px] flex-1 rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                style={{ borderColor: '#cbd5e1' }}
                placeholder={t('skillTrials.adj.addPlaceholder')}
                value={addSurface}
                onChange={(e) => setAddSurface(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addConcept()}
              />
              <select className="rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" style={{ borderColor: '#cbd5e1' }} value={addFacet} onChange={(e) => setAddFacet(e.target.value as SkillFacet)}>
                {FACETS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <button className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm text-slate-700 hover:bg-white" style={{ borderColor: '#cbd5e1' }} onClick={addConcept}>
                <FontAwesomeIcon icon={faPlus} className="text-xs" /> {t('skillTrials.adj.add')}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{t('skillTrials.adj.addNote')}</p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) exclude(e.target.value as TrialExclusionReason);
              }}
              className="rounded-lg border px-3 py-2 text-[13px] text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
              style={{ borderColor: '#e2e8f0' }}
              title={t('skillTrials.adj.excludeHint')}
            >
              <option value="">{t('skillTrials.adj.excludeLabel')}</option>
              <option value="COMPANY_REQUIREMENT">{t('skillTrials.adj.excludeCompany')}</option>
              <option value="TENDER_REQUIREMENT">{t('skillTrials.adj.excludeTender')}</option>
              <option value="NOT_SKILL_RELATED">{t('skillTrials.adj.excludeOther')}</option>
            </select>
            <button
              disabled={kept.length === 0 || saving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={submit}
            >
              <FontAwesomeIcon icon={faCheck} className="text-xs" />
              {saving ? t('skillTrials.adj.saving') : t('skillTrials.adj.save', { n: kept.length })}
            </button>
          </div>
        </>
      ) : (
        <Card>{t('skillTrials.adj.pickTwo')}</Card>
      )}

      {toast && (
        <div role="alert" className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm text-white shadow-lg ${toast.kind === 'error' ? 'bg-red-600' : 'bg-slate-800'}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

function seedConcepts(view: CriterionAdjudication): Draft[] {
  return view.rows.map((r) => ({
    id: crypto.randomUUID(),
    surfaces: [r.surface],
    facet: (r.facet ?? 'COMPETENCE') as SkillFacet,
    keep: true,
    fromA: r.from_a,
    fromB: r.from_b,
  }));
}

function provStyle(c: Draft): React.CSSProperties {
  if (c.manual) return { background: '#e2e8f0', color: '#475569' };
  if (c.fromA && c.fromB) return { background: '#e6e6fb', color: '#4f4fc9' };
  if (c.fromB) return { background: '#d6ecea', color: '#0c7d78' };
  return { background: '#f8efdb', color: '#a76a12' };
}

function ArmCard({ score }: { score: ArmScore }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[14px] border bg-white p-3 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
      <div className="mb-1 font-mono text-[12px] font-semibold">{score.label}</div>
      <div className="flex gap-6">
        <Metric label={t('skillTrials.adj.precision')} value={pct(score.precision)} sub={`${score.produced_hits}/${score.produced}`} />
        <Metric label={t('skillTrials.adj.recall')} value={pct(score.recall)} sub={`${score.gold_hits}/${score.gold_concepts}`} optimistic />
        {score.facet_accuracy != null && (
          <Metric label={t('skillTrials.adj.facet')} value={pct(score.facet_accuracy)} sub={`${score.facet_correct}/${score.facet_total}`} />
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, sub, optimistic }: { label: string; value: string; sub: string; optimistic?: boolean }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
        {label}
        {optimistic && <span className="rounded border px-1 text-[8px] font-bold text-amber-600" style={{ borderColor: '#d9a441' }}>{t('skillTrials.adj.optimistic')}</span>}
      </div>
      <div className="text-[22px] font-semibold tabular-nums leading-tight">{value}<span className="text-[13px] text-slate-400">%</span></div>
      <div className="font-mono text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</span>;
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-[14px] border bg-white py-12 text-center text-sm text-slate-500 shadow-sm" style={{ borderColor: '#e2e8f0' }}>{children}</div>;
}

function Select({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: { v: string; l: string }[]; label: string }) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
      <select className="rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" style={{ borderColor: '#cbd5e1' }} value={value} onChange={(e) => onChange(e.target.value)} disabled={options.length === 0}>
        {options.length === 0 && <option value="">—</option>}
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
